// @ts-check
// =============================================================================
// js/core/store.js — the ONE observable state store.
//
// WHAT THIS IS
//   The single source of truth for client state. It replaces the ~40 module-
//   level globals of the old client (userData, cards, duel, duels, deets,
//   loadedDeck, settings, guest, …) with named "slices" screens can read and
//   subscribe to. Nothing else in the app holds long-lived game state.
//
// WHO USES IT
//   - js/net/socket.js + js/net/http.js WRITE slices when the server speaks.
//   - Screens READ slices when rendering and SUBSCRIBE to re-render on change.
//
// HOW TO USE
//   import { store, setSlice, updateSlice, subscribe } from '../core/store.js';
//   const duels = store.lobby.duels;                 // read
//   setSlice('duel', newDuel);                       // replace + notify
//   const stop = subscribe('lobby', (lobby) => …);   // listen (returns stop fn)
//
// DESIGN NOTES
//   - Slices are replaced, not mutated, wherever practical — a set/update call
//     is the ONLY thing that notifies listeners, so silent mutation = stale UI.
//   - Listeners fire synchronously in subscription order. Keep them cheap
//     (schedule heavy re-renders yourself if needed).
// =============================================================================

/**
 * All client state, by slice. Screens must not invent new top-level globals —
 * add a slice here (with a comment) instead.
 */
export const store = {
	/** Identity + entitlement: see js/app/session.js for how this is derived.
	 *  tier: 'guest' | 'cheap' | 'paid'.  phase: 'boot'|'gate'|'login'|'ready'. */
	session: {
		phase: 'boot',
		tier: 'guest',
		reference: null,
		token: null,
		name: null,
		nickname: null,
		isGuest: true,
	},

	/** The full account blob from POST /login (decks, collection, settings…). */
	userData: null,

	/** Card definitions (server-filtered per user: arts, censor already applied). */
	cards: [],

	/** Hero definitions from the login payload. */
	heros: [],

	/** Live lobby feed: { duels: PublicDuel[], deets: {users, freeDuels} }. */
	lobby: { duels: [], deets: {} },

	/** The active duel (viewer-stripped, board oriented to us) or null. */
	duel: null,

	/** The currently selected deck (object from userData.decks or a prebuilt). */
	loadedDeck: null,

	/** Connection status: 'idle' | 'connected' | 'reconnecting' | 'down'. */
	connection: 'idle',

	/** Content/game version from the server (art cache-buster). */
	version: null,

	/** CDN base for card-art offload (from login; '' => disabled, load from GCS). */
	cdnBase: '',

	/** Number of unopened reward packs (drives the nav badge). */
	unopenedPacks: 0,
};

/** @type {Map<string, Set<Function>>} listeners per slice name */
const listeners = new Map();

/**
 * Subscribe to changes of one slice.
 * @param {string} slice   A key of `store` (e.g. 'duel', 'lobby').
 * @param {(value: any) => void} fn  Called with the new slice value.
 * @returns {() => void}   Call to unsubscribe.
 */
export function subscribe(slice, fn) {
	if (!listeners.has(slice)) {
		listeners.set(slice, new Set());
	}
	listeners.get(slice).add(fn);
	return () => listeners.get(slice)?.delete(fn);
}

/**
 * Replace a slice and notify its subscribers.
 * @param {string} slice  A key of `store`.
 * @param {any} value     The new value.
 * @returns {void}
 */
export function setSlice(slice, value) {
	store[slice] = value;
	notify(slice);
}

/**
 * Shallow-merge a patch into an object slice and notify.
 * @param {string} slice  A key of `store` whose value is an object.
 * @param {object} patch  Keys to overwrite.
 * @returns {void}
 */
export function updateSlice(slice, patch) {
	store[slice] = { ...store[slice], ...patch };
	notify(slice);
}

/**
 * Notify subscribers that a slice changed. Exposed for the rare case where a
 * large slice (userData) was mutated in place for performance and the caller
 * wants listeners to see it.
 * @param {string} slice
 * @returns {void}
 */
export function notify(slice) {
	const subs = listeners.get(slice);
	if (!subs) return;
	for (const fn of subs) {
		try {
			fn(store[slice]);
		} catch (error) {
			// One broken listener must not stop the others from updating.
			console.error(`store listener for "${slice}" threw:`, error);
		}
	}
}
